<template>
  <div class="input__container">
    <v-text-field
      v-model="search"
      :placeholder="$t(`${namespace}.startTypingToSearch`)"
      class="v-text-field--small pt-3 pb-3 px-3"
      prepend-inner-icon="search"
      :disabled="isLoading"
      :loading="isLoading"
      color="primary"
      outlined
      clear-icon="close"
      clearable
      hide-details
      @keyup="searchInput"
    />

    <ul class="list" :class="{ 'list--border-top': companies.length }">
      <li
        v-for="company in companies"
        :key="company.id"
        class="list__item"
      >
        <div @click="handleSetCompany(company)">
          <div class="text--ellipsis">{{ company.name }}</div>
          <span class="text--ellipsis">
            {{ company.displayName }} - {{ company.documentId | cnpj }}
          </span>
        </div>
      </li>
    </ul>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import { hasRole } from 'utils/functions'
import { isValidObject, isValidArray, catchError } from 'corp-front-vue-commons/src/utils/functions'
import debounce from 'lodash.debounce'

export default {
  name: 'AutoCompleteCompany',
  computed: {
    ...mapState({
      loggedIn: ({ session }) => session.status.loggedIn,
      selectedCompany: ({ company }) => company.selectedCompany
    })
  },
  data () {
    return {
      namespace: 'Components.AutoCompleteCompany',
      search: null,
      isLoading: false,
      companies: []
    }
  },
  methods: {
    ...mapActions({
      updateSelectedCompany: 'company/updateSelectedCompany',
      clearSelectedCompany: 'company/clearSelectedCompany',
      showAlert: 'snackbar/show'
    }),

    hasRole (group, role) {
      return hasRole(group, role)
    },

    isValidObject (val) {
      return isValidObject(val)
    },

    isValidArray (val) {
      return isValidArray(val)
    },

    handleSetCompany (company = null) {
      this.updateSearchInput(company)
      this.companies = []
      company && this.updateCompany(company)
    },

    updateSearchInput (company) {
      this.search = company && company.name ? company.name : null
    },

    isCNPJ (str) {
      return str.length === 18 && /\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}/.test(str)
    },

    removeNonNumericChars (str) {
      return str.replace(/\D/g, '')
    },

    async handleSearch () {
      if (!this.search) return

      this.isLoading = true

      try {
        this.search = this.isCNPJ(this.search)
          ? this.removeNonNumericChars(this.search)
          : this.search

        const { data: res } = await this.$apiService.company.get(
          { page: 1 },
          { limit: 50 },
          { search: this.search }
        )

        this.companies = res.data

        if (!this.isValidArray(res.data)) {
          this.showAlert({
            type: 'info',
            message: this.$t(`${this.namespace}.companyNotFound`)
          })
        }
      } catch (error) {
        catchError(error)
        this.loggedIn && this.showAlert({
          type: 'error',
          message: this.$t('Common.genericError')
        })
      }

      this.isLoading = false
    },

    async getWalletFinancialConfig (company) {
      try {
        const { data: walletFinancialConfig } = await this.$apiService.company.getWalletFinancialConfig(company.id)
        const { financialAccountId = null, periodicityConfigType = null, paymentMethod = null } = walletFinancialConfig.content

        this.updateSelectedCompany({
          ...company,
          financialAccountId,
          periodicityConfigType,
          paymentMethod
        })
      } catch (error) {
        catchError(error)
      }
    },

    async updateCompany (company) {
      try {
        let plan = null

        if (this.hasRole('company', 'ROLE_GET_COMPANY_ID_CONFIG')) {
          const { data: companyConfig } = await this.$apiService.company.getCompanyConfig(company.id)
          plan = this.isValidObject(companyConfig.data) && companyConfig.data.plan
            ? companyConfig.data.plan
            : null
        }

        const updateCompany = { ...company, plan }
        this.updateSelectedCompany(updateCompany)
        await this.getWalletFinancialConfig(updateCompany)
        this.$emit('change')
      } catch (error) {
        catchError(error)
        this.loggedIn && this.showAlert({
          type: 'error',
          message: this.$t('Common.genericError')
        })
      }
    },

    searchInput: debounce(function () {
      this.handleSearch()
    }, 1000)
  },
  created () {
    this.selectedCompany && this.updateSearchInput(this.selectedCompany)
  },
  watch: {
    search (val) {
      !val && this.handleSetCompany()
    },

    company (val) {
      val && this.handleSetCompany(val)
    }
  }
}
</script>

<style lang="scss" scoped>
.input__container {
  width: 100%;

  .v-text-field > .v-input__control > .v-input__slot {
    min-height: 40px;
  }
}

.list {
  list-style-type: none;
  max-height: 248px;
  max-width: 372px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;

  &.list--border-top {
    border-top: 1px solid $gray-4;
  }
}

.list__item {
  border-top: 1px solid $gray-4;
  cursor: pointer;
  line-height: 1.6;
  padding: 12px 16px;
  transition: background .26s;

  &:hover { background: rgba(0, 0, 0, 0.04); }
  &:first-child { border-top: none; }
  div { font-size: $base-font-size; }

  span {
    color: $gray-6;
    display: block;
    font-size: 11px;
  }
}
</style>
